import { combineEpics, ofType } from 'redux-observable';
import { map, switchMap } from 'rxjs/operators';

import { rs } from 'core/api';
import { reduxSwitch } from 'core/redux/tools';
import { internalizePage, timestamps } from 'core/redux/serializers';
import { ActionGeneratorBuilder, errorMap } from 'core/redux/actions';

const defaultPageSize = 10;

const internalize = timestamps(['createdAt', 'seenAt', 'sinceAt']).internalize;

export const actions = new ActionGeneratorBuilder('veta-airside-vehicles')
	.subtype('add', (add) => add.request({ vehicle: true, page: true }).success('data').fail().cancel())
	.subtype('load', (load) =>
		load.request({ no: false, size: false, sort: false, search: false }).success({ page: true }).fail().cancel(),
	)
	.subtype('update', (update) =>
		update.request({ id: true, vehicle: true, page: true }).success('data').fail().cancel(),
	)
	.subtype('remove', (remove) => remove.request({ id: true, page: true }).success().fail().cancel())
	.build();

const defaultState = {
	page: null,
	pending: false,
	error: null,
};

const addReducer = (state, action) => {
	switch (action.type) {
		case actions.add.request.type:
			return { ...state, pending: true, error: null };
		case actions.add.fail.type:
			return { ...state, pending: false, error: action.errorMessage };
		default:
			return state;
	}
};

const loadReducer = (state, action) => {
	switch (action.type) {
		case actions.load.request.type:
			return { ...state, pending: true, error: null };
		case actions.load.success.type:
			return { ...state, pending: false, page: action.page };
		case actions.load.fail.type:
			return { ...state, pending: false, error: action.errorMessage };
		default:
			return state;
	}
};

const updateReducer = (state, action) => {
	switch (action.type) {
		case actions.update.request.type:
			return { ...state, pending: true, error: null };
		case actions.update.fail.type:
			return { ...state, pending: false, error: action.errorMessage };
		default:
			return state;
	}
};

const removeReducer = (state, action) => {
	switch (action.type) {
		case actions.remove.request.type:
			return { ...state, pending: true, error: null };
		case actions.remove.fail.type:
			return { ...state, pending: false, error: action.errorMessage };
		default:
			return state;
	}
};

export const reducer = reduxSwitch([addReducer, loadReducer, updateReducer, removeReducer], defaultState);

const addEpic = (action$) => {
	return action$.pipe(
		ofType(actions.add.request.type),
		switchMap((action) =>
			rs(`veta/airside/vehicle`)
				.post(action.vehicle)
				.defer$()
				.pipe(
					map(() => actions.load.request(action.page)),
					errorMap(actions.add.fail),
				),
		),
	);
};

const loadEpic = (action$) => {
	return action$.pipe(
		ofType(actions.load.request.type),
		switchMap((action) =>
			rs(`veta/airside/vehicle/page`)
				.optional('no', action.no)
				.optional('sort', action.sort)
				.optional('search', action.search)
				.param('size', action.size | defaultPageSize)
				.defer$()
				.pipe(
					map(internalizePage(action.size | defaultPageSize, internalize)),
					map((page) => actions.load.success({ page })),
					errorMap(actions.load.fail),
				),
		),
	);
};

const updateEpic = (action$) => {
	return action$.pipe(
		ofType(actions.update.request.type),
		switchMap((action) =>
			rs(`veta/airside/vehicle/${action.id}`)
				.put(action.vehicle)
				.defer$()
				.pipe(
					map(() => actions.load.request(action.page)),
					errorMap(actions.update.fail),
				),
		),
	);
};

const removeEpic = (action$) => {
	return action$.pipe(
		ofType(actions.remove.request.type),
		switchMap((action) =>
			rs(`veta/airside/vehicle/${action.id}`)
				.delete()
				.defer$()
				.pipe(
					map(() => actions.load.request(action.page)),
					errorMap(actions.remove.fail),
				),
		),
	);
};

export const epic = combineEpics(addEpic, loadEpic, updateEpic, removeEpic);
